import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PureSupplierRelationshipsSelectionDetailedSupplierFragment } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import ComboboxServer from '@/modules/shared/components/combobox/server/ComboboxServer'
import { useGetPureSupplierRelationshipsSelectionDetailed } from '@/modules/shared/components/pure-comboboxes/hooks/useGetPureSupplierRelationshipsSelectionDetailed'

interface PureSupplierRelationshipsSelectionDetailedProps {
  onSelected: (e: PureSupplierRelationshipsSelectionDetailedSupplierFragment | null) => void
  defaultValue?: PureSupplierRelationshipsSelectionDetailedSupplierFragment
  onChipRemove?: () => void
  placeholder?: string
  hasError?: boolean
  errorMessage?: string
  floating?: boolean
  loading?: boolean
  showChips?: boolean
  disabled?: boolean
  apSystemId?: number
  purchaserId?: number
  onDisplay?: (e: PureSupplierRelationshipsSelectionDetailedSupplierFragment) => string
}

export function PureSupplierRelationshipsSelectionDetailed({
  onSelected,
  placeholder,
  hasError,
  errorMessage,
  floating,
  onChipRemove,
  defaultValue,
  loading,
  showChips = true,
  disabled,
  apSystemId,
  onDisplay,
  purchaserId,
}: PureSupplierRelationshipsSelectionDetailedProps) {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [hasOpenedCombobox, setHasOpenedCombobox] = useState(false)
  const { currentLegalEntity } = useSessionUser()
  const { suppliers, networkStatus, onFetchMore, pageInfo } = useGetPureSupplierRelationshipsSelectionDetailed({
    purchaserId: purchaserId ?? currentLegalEntity?.id,
    accountsPayableSystemId: apSystemId,
    searchText,
    skip: !hasOpenedCombobox,
  })

  return (
    <ComboboxServer
      fetchType="onOpen"
      onFirstOpen={() => setHasOpenedCombobox(true)}
      testId="pure-supplier-relationships-selection-detailed"
      networkStatus={networkStatus}
      placeholder={placeholder || t('general.selectSupplierPlaceholder', 'Select a Supplier...')}
      keyExtractor={(e) => String(e?.id)}
      onDisplay={(e) => (onDisplay ? onDisplay(e) : String(e.name))}
      items={suppliers}
      onInputChange={(e) => setSearchText(e)}
      onChipRemove={showChips ? onChipRemove : undefined}
      onSelected={onSelected}
      hasError={hasError}
      loading={loading}
      errorMessage={errorMessage}
      defaultValue={defaultValue}
      floating={floating}
      disabled={disabled}
      onFetchMore={onFetchMore}
      hasMore={!!pageInfo?.hasNextPage}
    />
  )
}
